import { styled } from "@mui/material/styles";
import { Box, Stack } from "@mui/material";

import {
  HubCardStack,
  HubHookFormDatePicker,
  HubButton,
} from "../../components";

const highlightColor = "white";
const inputColor = "blueMainTransparent";

export const BasicCardStack = styled(HubCardStack)(({ theme }) => ({
  padding: "5px",
  borderRadius: 0,
  borderColor: theme.palette.hubColors.grey,
  "span, p": { lineHeight: 1 },
  ".ItemCell-root": {
    paddingLeft: "5px",
  },
}));

export const StyledCardStack = styled(BasicCardStack)(({ theme }) => ({
  padding: "0px",
  ".MuiStack-root": {
    padding: "5px",
    width: "fit-content",
  },
}));

export const StyledCardRow = styled(BasicCardStack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
}));

export const StyledItemRow = styled(Stack, {
  shouldForwardProp: (prop) =>
    !["header", "topBorder", "highlight"].includes(prop),
})(({ theme, header, topBorder, highlight }) => ({
  display: "grid",
  columnGap: "2px",
  gridTemplateColumns: "1fr 100px",
  padding: "0px 0px 0px 10px",
  alignItems: "center",
  borderColor: theme.palette.hubColors.greyLight,
  gridAutoRows: "22px",
  position: "relative",
  ":hover": {
    ".AddButton": {
      display: "flex",
    },
  },
  ...(highlight
    ? {
        backgroundColor: theme.palette.hubColors[highlight],
      }
    : {}),
  ...(topBorder
    ? {
        borderTop: "1px solid",
      }
    : {
        borderBottom: "1px solid",
      }),
  ...(header
    ? {
        backgroundColor: theme.palette.hubColors.greyLight,
      }
    : {}),
  span: {
    fontSize: "11px",
    lineHeight: "11px",
  },
  ".ItemCell-subtitle": {
    fontSize: "9px",
    lineHeight: "9px",
  },
}));

export const StyledLabelStack = styled(Stack, {
  shouldForwardProp: (prop) => !["c1"].includes(prop),
})(({ c1 = "100px" }) => ({
  display: "grid",
  gridTemplateColumns: `${c1} 1fr`,
  alignItems: "end",
  columnGap: "5px",
  width: "100%",
}));

export const StyledGridStack = styled(Stack, {
  shouldForwardProp: (prop) => !["isLast"].includes(prop),
})(({ theme, isLast = false }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  justifyItems: "end",
  alignItems: "center",
  gap: "5px",
  width: "100%",
  ".PurchaseOrderLabel-value": {
    width: "100%",
    minWidth: "100px",
    padding: "2px",
    border: "1px solid",
    ...(isLast ? {} : { borderBottom: "0px" }),
    borderColor: theme.palette.hubColors.greyDark,
    backgroundColor: theme.palette.hubColors[highlightColor],
  },
}));

export const StyledFobStack = styled(Stack)(({ theme }) => ({
  justifyContent: "center",
  alignItems: "end",
  ".MuiFormControl-root": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    justifyItems: "end",
    alignItems: "center",
    gap: "5px",
    width: "100%",
    ".MuiTypography-inputLabelSmall": {
      margin: 0,
      ".MuiTypography-inputLabelSmall": {
        display: "none",
      },
    },
    ".isLast": {
      borderBottom: "1px solid !important",
      borderColor: theme.palette.hubColors.greyDark,
    },
    ".MuiInputBase-root, input, fieldset": {
      border: "0px",
    },
    ".MuiInputBase-root.Mui-error": {
      backgroundColor: theme.palette.hubColors.redMainErrorBg,
    },
    ".MuiInputBase-root": {
      width: "100%",
      minWidth: "100px",
      padding: "2px 2px 2px 4px",
      border: "1px solid",
      borderBottom: "0px",
      borderColor: theme.palette.hubColors.greyDark,
      backgroundColor: theme.palette.hubColors[inputColor],
      borderRadius: "0px",
      ".MuiSelect-select": {
        display: "flex",
        alignItems: "center",
      },
      ".MuiSvgIcon-root": {
        display: "none",
      },
      ".MuiTypography-root, input, textarea": {
        fontWeight: "700",
        color: theme.palette.hubColors.greyDark,
        lineHeight: "12px",
        fontSize: "12px",
      },
    },
  },
}));

export const StyledHeaderStack = styled(Stack)({
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
});

export const StyledUserBox = styled(Box)(({ theme }) => ({
  ".MuiFormControl-root": {
    display: "grid",
    gridTemplateColumns: "1fr",
    justifyItems: "start",
    alignItems: "center",
    width: "100%",
    overflow: "visible",
    ".MuiTypography-inputLabelSmall": {
      margin: 0,
      ".MuiTypography-inputLabelSmall": {
        display: "none",
      },
    },
    ".MuiInputBase-root, input, fieldset": {
      height: "100%",
      border: "0px",
    },
    "input, fieldset": {
      backgroundColor: "transparent",
    },
    ".MuiInputBase-root.Mui-error": {
      backgroundColor: theme.palette.hubColors.redMainErrorBg,
    },
    ".MuiInputBase-root": {
      width: "100%",
      height: "25px",
      padding: "5px 2px 5px 4px",
      border: "0px",
      borderBottom: "1px solid",
      borderColor: theme.palette.hubColors.greyDark,
      backgroundColor: theme.palette.hubColors[inputColor],
      borderRadius: "0px",
      ".MuiSelect-select": {
        display: "flex",
        alignItems: "center",
      },
      ".MuiSvgIcon-root": {
        display: "none",
      },
      ".MuiTypography-root, input, textarea": {
        fontWeight: 500,
        color: theme.palette.hubColors.greyDark,
      },
    },
  },
}));

export const StyledShipmentBox = styled(Stack)(({ theme }) => ({
  minWidth: "250px",
  padding: "0px !important",
  gap: "2px",
  ".MuiFormControl-root": {
    display: "grid",
    gridTemplateColumns: "1fr",
    justifyItems: "end",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    ".MuiStack-root": {
      padding: 0,
      backgroundColor: "transparent",
    },
    ".MuiTypography-inputLabelSmall": {
      margin: 0,
      ".MuiTypography-inputLabelSmall": {
        display: "none",
      },
    },
    ".MuiInputBase-root, input, fieldset": {
      border: "0px",
    },
    ".MuiAutocomplete-root": {
      width: "100%",
      minWidth: "100px",
      padding: "0px",
    },
    ".MuiInputBase-root.Mui-error": {
      backgroundColor: theme.palette.hubColors.redMainErrorBg,
    },
    ".MuiInputBase-root": {
      width: "100%",
      minWidth: "100px",
      padding: "5px !important",
      borderRadius: "0px",
      backgroundColor: theme.palette.hubColors[inputColor],
      ".MuiSelect-select": {
        display: "flex",
        alignItems: "center",
      },
      ".MuiSvgIcon-root": {
        display: "none",
      },
      ".MuiTypography-root, input, textarea": {
        fontWeight: "700",
        color: theme.palette.hubColors.greyDark,
        lineHeight: "12px",
        fontSize: "12px",
      },
    },
  },
}));

export const StyledHubHookFormDatePicker = styled(HubHookFormDatePicker)(
  ({ theme }) => ({
    ".MuiInputBase-root": {
      display: "grid",
      gridTemplateColumns: "auto 20px",
      "input::placeholder": {
        color: theme.palette.hubColors.greyDark,
      },
      ".MuiInputAdornment-root": {
        height: "fit-content",
        maxHeight: "fit-content",
        margin: 0,
        ".MuiButtonBase-root": {
          padding: "0px",
        },
      },
    },
    ".MuiSvgIcon-root": {
      display: "inline-block !important",
      width: "14px",
      height: "14px",
    },
  }),
);

export const StyledMoldPicker = styled(Stack)({
  position: "relative",
  ".MuiFormControl-root": {
    height: "36px",
    overflow: "hidden",
    ".MuiInputBase-root": {
      padding: "6px 4px 5px 4px !important",
    },
  },
  ".HubSearch-Results": {
    marginTop: "40px !important",
  },
  ":hover": {
    ".AddMoldButton": {
      display: "inline",
    },
  },
});

export const StyledMaterialPicker = styled(Stack)({
  position: "relative",
  ":hover": {
    ".AddMoldButton": {
      display: "inline",
    },
  },
});

export const StyledAddMoldButton = styled(HubButton)({
  display: "none",
  position: "absolute",
  top: 0,
  bottom: 0,
  right: "15px",
});
